import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { useApiQuery } from '@/hooks/use-api'
import { i18n } from '@/lib/i18n'

const BASE = '/zalo-broadcast-campaigns'
const KEY = ['zalo-broadcast-campaigns']

export type TrangThaiChienDich = 'draft' | 'scheduled' | 'running' | 'paused' | 'completed' | 'cancelled'
export type TrangThaiTaiKhoan = 'active' | 'paused' | 'done' | 'blocked'
export type TrangThaiNguoiNhan = 'pending' | 'sent' | 'failed' | 'skipped'
export type NguonNguoiNhan = 'all' | 'segment' | 'csv'

/** Một biến thể nội dung: chữ + ảnh kèm theo (tuỳ chọn). */
export interface BienTheNoiDung {
  text: string
  imageUrl?: string
}

export interface TaiKhoanChienDich {
  id: string
  channelAccountId: string
  status: TrangThaiTaiKhoan
  stoppedReason: string | null
  startedAt: string | null
  lastSentAt: string | null
  nextSendAt: string | null
  sentCount: number
  channelAccount?: { id: string; displayName: string | null; status: string }
}

export interface ChienDichNhanTin {
  id: string
  name: string
  source: NguonNguoiNhan
  segmentId: string | null
  status: TrangThaiChienDich
  messages: BienTheNoiDung[]
  optOutFooter: boolean
  dailyLimit: number
  dailyTotalLimit: number
  warmupEnabled: boolean
  sendWindowStart: number
  sendWindowEnd: number
  scheduledAt: string | null
  totalCount: number
  sentCount: number
  failedCount: number
  skippedCount: number
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  accounts: TaiKhoanChienDich[]
}

export interface ThongKeTaiKhoan {
  channelAccountId: string
  choGui: number
  daGui: number
  loi: number
  boQua: number
  guiHomNay: number
}

export interface ChiTietChienDichNhanTin extends ChienDichNhanTin {
  thongKeTaiKhoan: ThongKeTaiKhoan[]
  guiHomNay: number
}

export interface NguoiNhan {
  id: string
  channelAccountId: string
  friendUid: string
  displayName: string
  phone: string | null
  status: TrangThaiNguoiNhan
  orderIndex: number
  errorMessage: string | null
  variantIndex: number | null
  sentAt: string | null
}

export interface TranCung {
  maxDailyPerAccount: number
  maxDailyPerCampaign: number
  warmupLadder: number[]
  minGapSeconds: number
  maxGapSeconds: number
  maxVariants: number
  windowStartHour: number
  windowEndHour: number
  optOutFooter: string
}

export interface TaiKhoanBanBe {
  id: string
  displayName: string | null
  status: string
  soBan: number
}

export const NHAN_TRANG_THAI: Record<TrangThaiChienDich, string> = {
  get draft() { return i18n.t('marketing:broadcastCampaignStatus.draft') },
  get scheduled() { return i18n.t('marketing:broadcastCampaignStatus.scheduled') },
  get running() { return i18n.t('marketing:broadcastCampaignStatus.running') },
  get paused() { return i18n.t('marketing:broadcastCampaignStatus.paused') },
  get completed() { return i18n.t('marketing:broadcastCampaignStatus.completed') },
  get cancelled() { return i18n.t('marketing:broadcastCampaignStatus.cancelled') },
}

/** Tiến độ gửi 0..1. */
export function tienDo(c: Pick<ChienDichNhanTin, 'sentCount' | 'totalCount'>): number {
  return c.totalCount > 0 ? c.sentCount / c.totalCount : 0
}

/**
 * Ước số ngày chạy xong: tổng / (tổng tin mỗi ngày). Dùng để người tạo thấy ngay
 * "1.000 tin/ngày" thực tế cần bao nhiêu tài khoản.
 */
export function uocSoNgay(tong: number, soTaiKhoan: number, tran: Pick<TranCung, 'maxDailyPerAccount' | 'maxDailyPerCampaign'>, hanMucTk: number, tongNgay: number): number {
  const moiNgay = Math.min(soTaiKhoan * Math.min(hanMucTk, tran.maxDailyPerAccount), Math.min(tongNgay, tran.maxDailyPerCampaign))
  return moiNgay > 0 ? Math.ceil(tong / moiNgay) : 0
}

export function useChienDichNhanTin() {
  return useApiQuery<ChienDichNhanTin[]>(KEY, BASE, undefined, { placeholderData: (prev) => prev })
}

export function useTranCung() {
  return useApiQuery<TranCung>(['zalo-broadcast-limits'], `${BASE}/limits`)
}

export function useTaiKhoanBanBe() {
  return useApiQuery<TaiKhoanBanBe[]>(['zalo-broadcast-accounts'], `${BASE}/accounts`)
}

export function useChiTietChienDichNhanTin(id: string | undefined) {
  return useQuery<ChiTietChienDichNhanTin>({
    queryKey: ['zalo-broadcast-campaign', id],
    queryFn: async () => (await api.get<ChiTietChienDichNhanTin>(`${BASE}/${id}`)).data,
    enabled: !!id,
    refetchInterval: (q) => (['running', 'scheduled'].includes(q.state.data?.status ?? '') ? 30_000 : false),
  })
}

export function useNguoiNhan(id: string | undefined, params?: { status?: string; skip?: number; take?: number }) {
  return useApiQuery<{ rows: NguoiNhan[]; tong: number }>(
    ['zalo-broadcast-targets', id, params ?? {}],
    `${BASE}/${id}/targets`,
    params as Record<string, unknown> | undefined,
    { enabled: !!id, placeholderData: (prev) => prev },
  )
}

function useDungMutation<TInput>(goi: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: goi,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['zalo-broadcast-campaign'] })
      qc.invalidateQueries({ queryKey: ['zalo-broadcast-targets'] })
    },
  })
}

export interface TaoChienDichInput {
  name: string
  source: NguonNguoiNhan
  segmentId?: string | null
  messages: BienTheNoiDung[]
  optOutFooter: boolean
  dailyLimit: number
  dailyTotalLimit: number
  warmupEnabled: boolean
  sendWindowStart: number
  sendWindowEnd: number
}

export function useTaoChienDichNhanTin() {
  return useDungMutation<TaoChienDichInput>(async (input) =>
    (await api.post<ChienDichNhanTin>(BASE, input)).data,
  )
}

/** Tải ảnh kèm biến thể nội dung → trả URL để đặt vào messages[].imageUrl. */
export function useTaiAnhChienDich() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const { data } = await api.post<{ url: string }>(`${BASE}/upload-image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data.url
    },
  })
}

export function useNapNguoiNhan(id: string) {
  return useDungMutation<{
    accountIds: string[]
    source: NguonNguoiNhan
    segmentId?: string
    phones?: string[]
  }>(async (input) =>
    (await api.post<{ them: number; boQua: number; tong: number }>(`${BASE}/${id}/targets/import`, input)).data,
  )
}

/** start (kèm scheduledAt để hẹn giờ) | pause | resume | cancel. */
export function useDieuKhienChienDichNhanTin(id: string) {
  return useDungMutation<{ hanhDong: 'start' | 'pause' | 'resume' | 'cancel'; scheduledAt?: string }>(
    async ({ hanhDong, scheduledAt }) =>
      (await api.post(`${BASE}/${id}/${hanhDong}`, scheduledAt ? { scheduledAt } : undefined)).data,
  )
}

export function useDieuKhienTaiKhoanNhanTin(id: string) {
  return useDungMutation<{ accountId: string; hanhDong: 'pause' | 'resume' }>(
    async ({ accountId, hanhDong }) => (await api.post(`${BASE}/${id}/accounts/${accountId}/${hanhDong}`)).data,
  )
}
